/**
 * Orders list – data layer hooks.
 */
import { useMemo, useCallback } from 'react';
import { useQuery } from '@tanstack/react-query';
import { fetchOrders, type Order } from './api';
import { useAuthStore } from '../../../shared/auth/store';
import { isOnlineOrdersFeatureEnabled } from '../config';
import { ordersQueryKey } from './keys';

const STALE_MS = 1000 * 60 * 2;

export function useOrders() {
  const customer_id = useAuthStore(s => s.user?.id);
  const query = useQuery({
    queryKey: [...ordersQueryKey, customer_id],
    queryFn: () => fetchOrders(customer_id!),
    staleTime: STALE_MS,
    enabled: isOnlineOrdersFeatureEnabled() && customer_id != null,
  });

  const data = useMemo((): Order[] => (Array.isArray(query.data) ? query.data : []), [query.data]);
  const error = useMemo(() => (query.error instanceof Error ? query.error : null), [query.error]);
  const refetch = useCallback(() => query.refetch(), [query]);

  return useMemo(
    () => ({
      data,
      isLoading: query.isLoading,
      error,
      refetch,
    }),
    [data, query.isLoading, error, refetch],
  );
}

export type { Order } from './api';
